import React, { useState, useEffect, useMemo } from 'react';
import { listStores, removeStore, createStore, updateStore, deleteStore } from '../../api/stores';
import { listGroups } from '../../api/groups';
import { getBreaklistData } from '../../api/timekeep';
import { getRates, postRates, deleteStoreRate } from '../../api/rates';
import {
  getAllowances,
  createAllowance,
  updateAllowance,
  deleteAllowance,
  assignToStore,
  removeStoreAssignment
} from '../../api/allowances';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Chip,
  CircularProgress,
  Skeleton,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Fade,
  Pagination,
  Select,
  MenuItem,
  Alert,
  TableSortLabel,
  Popover,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  FormGroup,
  FormControlLabel,
  Divider,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel as MuiFormControlLabel,
  Switch,
  Autocomplete,
  Grid,
  RadioGroup,
  Radio
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Store as StoreIcon,
  Group as GroupIcon,
  LocationOn as LocationIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon } from '@iconify/react';
import { exportToCSV, exportToPrint, exportToPDF, formatDataForExport } from '../../utils/exportUtils';
import { useAuth } from '../../context/AuthContext';

const normalizeAccessValue = (value) => String(value || '').trim().toLowerCase();

const normalizeAccessKey = (value) => normalizeAccessValue(value)
  .replace(/\bphilippines\b/g, 'phils')
  .replace(/\bcorporation\b/g, 'corp')
  .replace(/\bincorporated\b/g, 'inc')
  .replace(/[^a-z0-9]/g, '');

const accessMatches = (accessValues, candidate) => {
  const normalizedCandidate = normalizeAccessValue(candidate);
  if (!normalizedCandidate) return false;

  const candidateKey = normalizeAccessKey(candidate);
  return accessValues.some((accessValue) => {
    const accessKey = normalizeAccessKey(accessValue);
    return normalizedCandidate === accessValue ||
      normalizedCandidate.includes(accessValue) ||
      accessValue.includes(normalizedCandidate) ||
      (candidateKey && accessKey && candidateKey === accessKey);
  });
};

const Stores = () => {
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [refreshing, setRefreshing] = useState(false);

  // State for store data from API
  const [stores, setStores] = useState([]);
  const [storeData, setStoreData] = useState(null);

  // State for groups (for dropdown)
  const [groups, setGroups] = useState([]);

  // State for control panel
  const [selectedStoreForPanel, setSelectedStoreForPanel] = useState(null);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  
  // State for breaklist data
  const [breaklistData, setBreaklistData] = useState([]);
  const [loadingBreaklist, setLoadingBreaklist] = useState(false);

  // Sorting state
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');

  // Filtering state
  const [filters, setFilters] = useState({
    groups: [],
    status: []
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterColumn, setFilterColumn] = useState(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [selectedStore, setSelectedStore] = useState(null);
  const [formData, setFormData] = useState({
    store: '',
    group_id: '',
    location: '',
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Rates dialog state
  const [ratesDialogOpen, setRatesDialogOpen] = useState(false);
  const [ratesFormData, setRatesFormData] = useState({
    dailyRate: 570,
    holidayRate: 1,
    holidayOTRate: 2.6,
    holidayRestDayRate: 0.6,
    holidayRestDayOTRate: 3.38,
    nightshiftRate: 0.1,
    overtimeRate: 1.25,
    sssRate: 532.5,
    pagibigRate: 100,
    philhealthRate: 297.35,
    restDayDutyRate: 0.3,
    restDayOTRate: 1.69,
    specialHolidayRate: 0.3,
    specialHolidayOTRate: 1.69,
    specialHolidayRDRate: 0.5,
    specialHolidayRDOTRate: 1.95,
    allowance: '',
    latesEnabled: true,
    undertimeEnabled: true
  });
  const [ratesFormErrors, setRatesFormErrors] = useState({});
  const [savingRates, setSavingRates] = useState(false);

  // Delete rates confirmation state
  const [deleteRatesDialogOpen, setDeleteRatesDialogOpen] = useState(false);
  
  // Allowances dialog state
  const [allowancesDialogOpen, setAllowancesDialogOpen] = useState(false);
  const [allowances, setAllowances] = useState([]);
  const [storeAllowances, setStoreAllowances] = useState([]);
  const [allowanceDialogOpen, setAllowanceDialogOpen] = useState(false);
  const [allowanceDialogMode, setAllowanceDialogMode] = useState('add');
  const [selectedAllowance, setSelectedAllowance] = useState(null);
  const [allowanceFormData, setAllowanceFormData] = useState({
    name: '',
    type: 'billing',
    amount: 0,
    rate: 0,
    description: '',
    is_active: true,
    is_taxable: true,
    admin: true
  });
  const [allowanceFormErrors, setAllowanceFormErrors] = useState({});
  const [loadingAllowances, setLoadingAllowances] = useState(false);
  const [allowanceSearchTerm, setAllowanceSearchTerm] = useState('');
  const [savingAllowance, setSavingAllowance] = useState(false);
  
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super-admin' || user?.role === 'admin';
  const groupAccessValues = useMemo(
    () => (Array.isArray(user?.groupAccess) ? user.groupAccess.map(normalizeAccessValue).filter(Boolean) : []),
    [user]
  );
  const storeAccessValues = useMemo(
    () => (Array.isArray(user?.storeAccess) ? user.storeAccess.map(normalizeAccessValue).filter(Boolean) : []),
    [user]
  );

  const userCanAccessGroup = (groupName) => (
    isSuperAdmin || accessMatches(groupAccessValues, groupName)
  );

  const userCanAccessStore = (store) => (
    isSuperAdmin ||
    accessMatches(groupAccessValues, store?.group_name || store?.group) ||
    accessMatches(storeAccessValues, store?.store || store?.name)
  );

  // Load stores and groups from API
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      refreshTableData();
      enqueueSnackbar('Data loaded successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
      enqueueSnackbar(err.message || 'Failed to load data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Refresh only table data without full page loading
  const refreshTableData = async () => {
    setTableLoading(true);
    try {
      // Load stores
      const storesResponse = await listStores();
      if (storesResponse.status === 'success') {
        // Transform the data to match the expected format
        const accessibleStoreRows = (storesResponse.data.stores || []).filter(userCanAccessStore);
        const transformedStores = accessibleStoreRows.map((storeObj, index) => ({
          id: storeObj.id || index + 1,
          _id: storeObj._id,
          name: storeObj.store,
          store: storeObj.store,
          group: storeObj.group_name || 'Unknown Group',
          group_id: storeObj.group_id,
          location: storeObj.location || '',
          active: true,
          user_id: storeObj.user_id,
          created_at: storeObj.created_at,
          updated_at: storeObj.updated_at
        }));

        setStores(transformedStores);
        setStoreData({
          ...storesResponse.data,
          stores: accessibleStoreRows
        });
      }

      // Load groups for dropdown (in case new groups were added)
      const groupsResponse = await listGroups();
      if (groupsResponse.status === 'success') {
        const accessibleGroups = isSuperAdmin
          ? groupsResponse.data.groups
          : (groupsResponse.data.groups || []).filter((group) => userCanAccessGroup(group.name));
        setGroups(accessibleGroups);
      }
    } catch (err) {
      console.error('Error refreshing data:', err);
      enqueueSnackbar('Failed to refresh data', { variant: 'error' });
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [enqueueSnackbar, user]);

  // Sorting function
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Filter handler
  const handleFilterClick = (event, column) => {
    setFilterAnchorEl(event.currentTarget);
    setFilterColumn(column);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setFilterColumn(null);
  };

  const handleFilterChange = (column, value) => {
    setFilters(prev => ({
      ...prev,
      [column]: prev[column].includes(value)
        ? prev[column].filter(item => item !== value)
        : [...prev[column], value]
    }));
  };

  const clearFilters = () => {
    setFilters({ groups: [], status: [] });
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Get unique values for filters
  const uniqueGroups = useMemo(() => {
    return [...new Set(stores.map(store => store.group))].sort();
  }, [stores]);

  const uniqueStatuses = ['Active', 'Inactive'];

  // Function to fetch breaklist data from timekeep API
  const fetchBreaklistData = async () => {
    if (!selectedStoreForPanel) {
      enqueueSnackbar('Please select a store first', { variant: 'warning' });
      return;
    }

    setLoadingBreaklist(true);
    try {
      const fromDateStr = fromDate.toISOString().split('T')[0];
      const toDateStr = toDate.toISOString().split('T')[0];

      const response = await getBreaklistData(selectedStoreForPanel.name, fromDateStr, toDateStr);

      if (response.success) {
        setBreaklistData(response.data);
        enqueueSnackbar(response.message, { variant: 'success' });
      } else {
        setBreaklistData([]);
        enqueueSnackbar(response.message, { variant: 'info' });
      }
    } catch (error) {
      console.error('Error fetching breaklist data:', error);
      enqueueSnackbar('Failed to fetch breaklist data', { variant: 'error' });
      setBreaklistData([]);
    } finally {
      setLoadingBreaklist(false);
    }
  };

  // Enhanced filter and sorting logic
  const filteredAndSortedStores = useMemo(() => {
    let result = stores.filter(store => {
      // Text search
      const matchesSearch = !searchTerm ||
        store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        store.group.toLowerCase().includes(searchTerm.toLowerCase()) ||
        store.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        store.id.toString().includes(searchTerm);

      // Group filter
      const matchesGroup = filters.groups.length === 0 ||
        filters.groups.includes(store.group);

      // Status filter
      const matchesStatus = filters.status.length === 0 ||
        filters.status.includes(store.active ? 'Active' : 'Inactive');

      return matchesSearch && matchesGroup && matchesStatus;
    });

    // Sorting
    result.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle different data types
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
      }
    });


    return result;
  }, [searchTerm, stores, filters, orderBy, order]);

  const totalPages = Math.ceil(breaklistData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedEmployees = breaklistData.slice(startIndex, startIndex + rowsPerPage);



  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshTableData();
      enqueueSnackbar('Data refreshed successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error refreshing stores:', err);
      enqueueSnackbar('Failed to refresh data', { variant: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  const handleExport = (type) => {
    try {
      const dataToExport = formatDataForExport(filteredAndSortedStores, 'stores');

      switch (type) {
        case 'CSV':
          exportToCSV(dataToExport, 'stores_export');
          enqueueSnackbar('CSV exported successfully', { variant: 'success' });
          break;
        case 'Print':
          exportToPrint(dataToExport, 'Stores Report');
          enqueueSnackbar('Print dialog opened', { variant: 'info' });
          break;
        case 'PDF':
          exportToPDF(dataToExport, 'stores_export', 'Stores Report');
          enqueueSnackbar('PDF export initiated', { variant: 'info' });
          break;
        default:
          enqueueSnackbar(`Unknown export type: ${type}`, { variant: 'warning' });
      }
    } catch (error) {
      console.error('Export error:', error);
      enqueueSnackbar('Export failed', { variant: 'error' });
    }
  };

  // Handle delete store
  const handleDeleteStore = async (storeId, storeName) => {
    if (!window.confirm(`Are you sure you want to delete "${storeName}"?`)) {
      return;
    }

    try {
      const response = await removeStore(storeId);
      if (response.status === 'success') {
        // Remove from local state
        setStores(prevStores => prevStores.filter(store => store.store !== storeName));
        enqueueSnackbar(`Store "${storeName}" deleted successfully`, { variant: 'success' });
      } else {
        throw new Error(response.message || 'Failed to delete store');
      }
    } catch (err) {
      console.error('Error deleting store:', err);
      enqueueSnackbar(err.message || 'Failed to delete store', { variant: 'error' });
    }
  };

  // Handle edit store
  const handleEditStore = (storeId, storeName) => {
    const store = stores.find(s => s._id === storeId);
    if (store) {
      handleOpenDialog('edit', store);
    } else {
      enqueueSnackbar('Store not found', { variant: 'error' });
    }
  };

  // Dialog handlers
  const handleOpenDialog = (mode = 'add', store = null) => {
    setDialogMode(mode);
    setSelectedStore(store);

    if (mode === 'edit' && store) {
      setFormData({
        store: store.name || '',
        group_id: store.group_id || '',
        location: store.location || '',
        isActive: store.active
      });
    } else {
      setFormData({
        store: '',
        group_id: '',
        location: '',
        isActive: true
      });
    }

    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedStore(null);
    setFormData({
      store: '',
      group_id: '',
      location: '',
      isActive: true
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.store.trim()) {
      errors.store = 'Store name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (dialogMode === 'add') {
        await createStore(formData);
        enqueueSnackbar('Store created successfully', { variant: 'success' });
      } else if (dialogMode === 'edit' && selectedStore) {
        await updateStore(selectedStore._id, formData);
        enqueueSnackbar('Store updated successfully', { variant: 'success' });
      }

      // Refresh only table data without full page loading
      await refreshTableData();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving store:', error);
      enqueueSnackbar(error.message || 'Failed to save store', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Rates dialog handlers
  const handleOpenRatesDialog = async () => { 

    if (!selectedStoreForPanel) {
      enqueueSnackbar('Please select a store first', { variant: 'warning' });
      return;
    }
    else {
      const data = {
        id: selectedStoreForPanel.name
      }
      const rates = await getRates(data);
      if(rates?.data) {
        if(rates.data.uid !== "1"){
          setRatesFormData(rates.data)
        }
      }
    }
    setRatesDialogOpen(true);
  };

  const handleCloseRatesDialog = () => {
    setRatesDialogOpen(false);
    setRatesFormData({
      dailyRate: 570,
      holidayRate: 1,
      holidayOTRate: 2.6,
      holidayRestDayRate: 0.6,
      holidayRestDayOTRate: 3.38,
      nightshiftRate: 0.1,
      overtimeRate: 1.25,
      sssRate: 532.5,
      pagibigRate: 100,
      philhealthRate: 297.35,
      restDayDutyRate: 0.3,
      restDayOTRate: 1.69,
      specialHolidayRate: 0.3,
      specialHolidayOTRate: 1.69,
      specialHolidayRDRate: 0.5,
      specialHolidayRDOTRate: 1.95,
      allowance: '',
      latesEnabled: true,
      undertimeEnabled: true
    });
    setRatesFormErrors({});
  };

  const handleRatesInputChange = (field, value) => {
    if (value !== '' && Number(value) < 0) {
      return;
    }
    setRatesFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (ratesFormErrors[field]) {
      setRatesFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleRatesFormSubmit = async () => {
    setSavingRates(true);
    try {
      const { _id, ...rest } = ratesFormData; // strip _id

      const ratesFormDataFormatted = {
        ...rest,
        uid: selectedStoreForPanel.name
      };
      const result = await postRates(ratesFormDataFormatted)
      console.log('Saving rates:', result);
      enqueueSnackbar('Rates saved successfully', { variant: 'success' });
      handleCloseRatesDialog();
    } catch (error) {
      console.error('Error saving rates:', error);
      enqueueSnackbar(error.message || 'Failed to save rates', { variant: 'error' });
    } finally {
      setSavingRates(false);
    }
  };

  // Delete rates handlers
  const handleOpenDeleteRatesDialog = () => {
    if (!selectedStoreForPanel) {
      enqueueSnackbar('Please select a store first', { variant: 'warning' });
      return;
    }
    setDeleteRatesDialogOpen(true);
  };

  const handleCloseDeleteRatesDialog = () => {
    setDeleteRatesDialogOpen(false);
  };

  // Allowances dialog handlers
  const handleOpenAllowancesDialog = async () => {
    if (!selectedStoreForPanel) {
      enqueueSnackbar('Please select a store first', { variant: 'warning' });
      return;
    }
    await loadAllowances();
    await loadStoreAllowances(selectedStoreForPanel.name);
    setAllowancesDialogOpen(true);
  };

  const handleCloseAllowancesDialog = () => {
    setAllowancesDialogOpen(false);
    setAllowanceSearchTerm('');
  };

  // Load all allowances
  const loadAllowances = async () => {
    try {
      setLoadingAllowances(true);
      const data = {
        store: selectedStoreForPanel.name
      }
      const response = await getAllowances(data);
      if (response.status === 'success') {
        setAllowances(response.data || []);
      }
    } catch (error) {
      console.error('Error loading allowances:', error);
      enqueueSnackbar('Failed to load allowances', { variant: 'error' });
    } finally {
      setLoadingAllowances(false);
    }
  };

  // Load allowances assigned to current store
  const loadStoreAllowances = async (storeName) => {
    if (!storeName) return;
    try {
      const response = await getAllowances({ store: storeName });
      if (response.status === 'success') {
        const allAllowances = response.data || [];
        const assignedAllowances = allAllowances.filter(allowance => 
          allowance.assignments?.stores?.includes(storeName)
        );
        setStoreAllowances(assignedAllowances);
      }
    } catch (error) {
      console.error('Error loading store allowances:', error);
    }
  };

  // Handle allowance dialog open
  const handleOpenAllowanceDialog = (mode = 'add', allowance = null) => {
    setAllowanceDialogMode(mode);
    if (mode === 'edit' && allowance) {
      setSelectedAllowance(allowance);
      setAllowanceFormData({
        name: allowance.name || '',
        type: allowance.type || 'billing',
        amount: allowance.amount || 0,
        rate: allowance.rate || 0,
        description: allowance.description || '',
        is_active: allowance.is_active !== undefined ? allowance.is_active : true,
        is_taxable: allowance.is_taxable !== undefined ? allowance.is_taxable : true,
        admin: allowance.admin !== undefined ? allowance.admin : true,
      });
    } else {
      setSelectedAllowance(null);
      setAllowanceFormData({
        name: '',
        type: 'billing',
        amount: 0,
        rate: 0,
        description: '',
        is_active: true,
        is_taxable: true,
        admin: true
      });
    }
    setAllowanceFormErrors({});
    setAllowanceDialogOpen(true);
  };

  // Handle allowance dialog close
  const handleCloseAllowanceDialog = () => {
    setAllowanceDialogOpen(false);
    setSelectedAllowance(null);
    setAllowanceFormData({
      name: '',
      type: 'fixed_amount',
      amount: 0,
      rate: 0,
      description: '',
      is_active: true,
      is_taxable: true
    });
    setAllowanceFormErrors({});
  };

  // Validate allowance form
  const validateAllowanceForm = () => {
    const errors = {};
    if (!allowanceFormData.name.trim()) {
      errors.name = 'Allowance name is required';
    }
    if (allowanceFormData.type === 'fixed_amount' && (!allowanceFormData.amount || allowanceFormData.amount <= 0)) {
      errors.amount = 'Amount must be greater than 0';
    }
    if (allowanceFormData.type === 'rate_based' && (!allowanceFormData.rate || allowanceFormData.rate <= 0)) {
      errors.rate = 'Rate must be greater than 0';
    }
    setAllowanceFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle allowance form submit
  const handleAllowanceFormSubmit = async () => {
    if (!validateAllowanceForm()) return;

    setSavingAllowance(true);
    try {
      const allowanceData = {
        name: allowanceFormData.name,
        type: allowanceFormData.type,
        description: allowanceFormData.description,
        is_active: allowanceFormData.is_active,
        is_taxable: allowanceFormData.is_taxable,
        admin: allowanceFormData.admin,
        amount: allowanceFormData.amount,
        assignments: {
          personnel: [],
          stores: selectedStoreForPanel.name,
          groups: []
        }
      };

      // if (allowanceFormData.type === 'fixed_amount') {
      //   allowanceData.amount = allowanceFormData.amount;
      // } else {
      //   allowanceData.rate = allowanceFormData.rate;
      // }

      let allowanceId;
      if (allowanceDialogMode === 'add') {
        const response = await createAllowance(allowanceData);
        if (response.status === 'success') {
          allowanceId = response.data._id;
          enqueueSnackbar('Allowance created successfully', { variant: 'success' });
        }
      } else {
        await updateAllowance(selectedAllowance._id, allowanceData);
        allowanceId = selectedAllowance._id;
        enqueueSnackbar('Allowance updated successfully', { variant: 'success' });
      }

      // Assign the allowance to the store
      if (allowanceId && selectedStoreForPanel) {
        try {
          await assignToStore(allowanceId, [selectedStoreForPanel.name]);
        } catch (assignError) {
          console.error('Error assigning allowance to store:', assignError);
        }
      }

      await loadAllowances();
      if (selectedStoreForPanel) {
        await loadStoreAllowances(selectedStoreForPanel.name);
      }
      handleCloseAllowanceDialog();
    } catch (error) {
      console.error('Error saving allowance:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save allowance';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSavingAllowance(false);
    }
  };

  // Handle delete allowance
  const handleDeleteAllowance = async (allowance) => {
    if (!window.confirm(`Are you sure you want to delete "${allowance.name}"?`)) {
      return;
    }

    try {
      await deleteAllowance(allowance._id);
      enqueueSnackbar('Allowance deleted successfully', { variant: 'success' });
      await loadAllowances();
      if (selectedStoreForPanel) {
        await loadStoreAllowances(selectedStoreForPanel.name);
      }
    } catch (error) {
      console.error('Error deleting allowance:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete allowance';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Handle assign/unassign allowance to store
  const handleToggleAllowanceAssignment = async (allowance) => {
    if (!selectedStoreForPanel) return;

    const isAssigned = allowance.assignments?.stores?.includes(selectedStoreForPanel.name);
    
    try {
      if (isAssigned) {
        await removeStoreAssignment(allowance._id, selectedStoreForPanel.name);
        enqueueSnackbar('Allowance unassigned from store', { variant: 'success' });
      } else {
        await assignToStore(allowance._id, [selectedStoreForPanel.name]);
        enqueueSnackbar('Allowance assigned to store', { variant: 'success' });
      }
      await loadAllowances();
      await loadStoreAllowances(selectedStoreForPanel.name);
    } catch (error) {
      console.error('Error toggling allowance assignment:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update assignment';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleConfirmDeleteRates = async () => {
    setSavingRates(true);
    try {
      const data = {
        store: selectedStoreForPanel.store
      }
      console.log(data)
      const result = await deleteStoreRate(data);
      console.log(result.data)
      enqueueSnackbar('Rates deleted successfully', { variant: 'success' });
      handleCloseDeleteRatesDialog();
    } catch (error) {
      console.error('Error deleting rates:', error);
      enqueueSnackbar(error.message || 'Failed to delete rates', { variant: 'error' });
    } finally {
      setSavingRates(false);
    }
  };

  // Handle create store
  const handleCreateStore = () => {
    handleOpenDialog('add');
  };

  const getGroupColor = (group) => {
    const colors = {
      'Sinjay Surfing Group': { bg: '#dcfce7', color: '#166534' },
      'SM Cebuano Group': { bg: '#dbeafe', color: '#1d4ed8' },
      'Jollibee Foods Corporation': { bg: '#fef3c7', color: '#d97706' },
      'Ayala Malls': { bg: '#f3e8ff', color: '#7c3aed' },
      'Robinsons Retail': { bg: '#fce7f3', color: '#be185d' },
      'Mercury Drug': { bg: '#f0f9ff', color: '#0369a1' },
    };
    return colors[group] || { bg: '#f3f4f6', color: '#374151' };
  };



  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="200px" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="300px" height={20} sx={{ mb: 3 }} />
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="rectangular" width="200px" height={36} />
            <Skeleton variant="rectangular" width="120px" height={36} />
          </Box>
          {[...Array(5)].map((_, index) => (
            <Skeleton key={index} variant="rectangular" width="100%" height={65} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Paper>
      </Box>
    );
  }

  return (
    <Fade in timeout={600}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 2,
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1" 
              sx={{ fontWeight: 'bold', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <StoreIcon sx={{ color: '#12C1DC' }} />
              Store Management
            </Typography>
            <Tooltip title="Refresh data">
              <IconButton 
                onClick={handleRefresh} 
                disabled={refreshing}
                sx={{ 
                  color: '#12C1DC',
                  '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' }
                }}
              >
                {refreshing ? (
                  <CircularProgress size={20} sx={{ color: '#12C1DC' }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link underline="hover" color="inherit" href="/">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="/masterfiles">
              Masterfiles
            </Link>
            <Typography color="text.primary">Stores</Typography>
          </Breadcrumbs>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          </motion.div>
        )}

        {/* Control Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
                  Select Store
                </Typography>
                <Autocomplete
                  value={selectedStoreForPanel}
                  onChange={(event, newValue) => setSelectedStoreForPanel(newValue)}
                  options={stores}
                  getOptionLabel={(option) => option?.name || ''}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search Store"
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': { borderColor: '#12C1DC' },
                          '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StoreIcon sx={{ color: '#12C1DC', fontSize: '1rem' }} />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {option.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.group} • {option.location}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
                  From
                </Typography>
                <TextField
                  type="date"
                  value={fromDate.toISOString().split('T')[0]}
                  onChange={(e) => setFromDate(new Date(e.target.value))}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
                  To
                </Typography>
                <TextField
                  type="date"
                  value={toDate.toISOString().split('T')[0]}
                  onChange={(e) => setToDate(new Date(e.target.value))}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#374151', visibility: 'hidden' }}>
                  Actions
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={fetchBreaklistData}
                    disabled={loadingBreaklist || !selectedStoreForPanel}
                    sx={{
                      bgcolor: '#10b981',
                      '&:hover': { bgcolor: '#059669' },
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      '&:disabled': {
                        bgcolor: '#e5e7eb',
                        color: '#9ca3af'
                      }
                    }}
                  >
                    {loadingBreaklist ? 'Loading...' : 'View'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleOpenRatesDialog}
                    disabled={!selectedStoreForPanel}
                    sx={{
                      bgcolor: '#10b981',
                      '&:hover': { bgcolor: '#059669' },
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      '&:disabled': {
                        bgcolor: '#e5e7eb',
                        color: '#9ca3af'
                      }
                    }}
                  >
                    Set Rates
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleOpenAllowancesDialog}
                    disabled={!selectedStoreForPanel}
                    sx={{
                      bgcolor: '#12C1DC',
                      '&:hover': { bgcolor: '#0FABDB' },
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      '&:disabled': {
                        bgcolor: '#e5e7eb',
                        color: '#9ca3af'
                      }
                    }}
                  >
                    Manage Allowances
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleOpenDeleteRatesDialog}
                    disabled={!selectedStoreForPanel}
                    sx={{
                      bgcolor: '#ef4444',
                      '&:hover': { bgcolor: '#dc2626' },
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      '&:disabled': {
                        bgcolor: '#e5e7eb',
                        color: '#9ca3af'
                      }
                    }}
                  >
                    Delete Rates
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>

        {/* Controls and Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            {/* Table Controls */}
            <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderBottom: '1px solid #e5e7eb' }}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    Show
                  </Typography>
                  <Select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(e.target.value);
                      setCurrentPage(1);
                    }}
                    size="small"
                    sx={{ minWidth: 80 }}
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                    entries
                  </Typography>
                </Box>

                <Box sx={{
                  display: 'flex',
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-end' }
                }}>
                  <Tooltip title="Export CSV">
                    <IconButton
                      size="small"
                      onClick={() => handleExport('CSV')}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <ExportIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export Excel">
                    <IconButton
                      size="small"
                      onClick={() => handleExport('Excel')}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <Icon icon="mdi:microsoft-excel" width="18" height="18" style={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Print">
                    <IconButton
                      size="small"
                      onClick={() => handleExport('Print')}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <PrintIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export PDF">
                    <IconButton
                      size="small"
                      onClick={() => handleExport('PDF')}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <PdfIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            {/* Enhanced Table */}
            <TableContainer sx={{ maxHeight: '70vh', overflow: 'auto', position: 'relative' }}>
              {(tableLoading || loadingBreaklist) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    borderRadius: 1
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={40} sx={{ color: '#12C1DC' }} />
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                        {loadingBreaklist ? 'Loading breaklist data...' : 'Loading stores...'}
                      </Typography>
                    </Box>
                  </motion.div>
                </Box>
              )}
              <Table
                stickyHeader
                size={isMobile ? "small" : "medium"}
                aria-label="breaklist table"
              >
                <TableHead>
                  <TableRow sx={{
                    '& .MuiTableCell-root': {
                      backgroundColor: '#f9fafb',
                      borderBottom: '2px solid #e5e7eb'
                    }
                  }}>
                    <TableCell sx={{
                      fontWeight: 600,
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Employee Name
                    </TableCell>
                    <TableCell sx={{
                      fontWeight: 600,
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Days Work
                    </TableCell>
                    <TableCell sx={{
                      fontWeight: 600,
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Hours Work
                    </TableCell>
                    <TableCell sx={{
                      fontWeight: 600,
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Tardiness (Minutes)
                    </TableCell>
                    <TableCell sx={{
                      fontWeight: 600,
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Overtime (Hours)
                    </TableCell>
                    <TableCell sx={{
                      fontWeight: 600,
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Special holiday
                    </TableCell>
                    <TableCell sx={{
                      fontWeight: 600,
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Legal holiday
                    </TableCell>
                    <TableCell sx={{
                      fontWeight: 600,
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Night Diff (Hours)
                    </TableCell>
                    <TableCell sx={{
                      fontWeight: 600,
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      RD Work (Hours)
                    </TableCell>
                    <TableCell sx={{
                      fontWeight: 600,
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Legal Holiday (Hours)
                    </TableCell>
                    <TableCell sx={{
                      fontWeight: 600,
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Special Holiday (Hours)
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <AnimatePresence>
                    {breaklistData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            {loadingBreaklist ? 'Loading data...' : 'No data available. Please select a store and click View to load breaklist data.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedEmployees.map((employee, index) => {
                        const isEvenRow = index % 2 === 0;
                        return (
                          <motion.tr
                            key={employee.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2, delay: index * 0.05 }}
                            style={{
                              display: 'table-row',
                              backgroundColor: isEvenRow ? '#ffffff' : '#f8f9fa'
                            }}
                            sx={{
                              '&:hover': {
                                backgroundColor: isEvenRow ? '#f0f0f0 !important' : '#e9ecef !important'
                              },
                              '& > .MuiTableCell-root': {
                                borderRight: '1px solid #e0e0e0',
                                borderBottom: '1px solid #e0e0e0'
                              },
                              '& > .MuiTableCell-root:last-child': {
                                borderRight: 'none'
                              }
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {employee.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {employee.daysWork}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {employee.hoursWork}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {employee.tardinessMinutes}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {employee.overtimeHours}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {employee.nightDiffHours}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {employee.rdWorkHours}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {employee.legalHolidayHours}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {employee.specialHolidayHours}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {employee.legalHolidayHours}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {employee.specialHolidayHours}
                              </Typography>
                            </TableCell>
                          </motion.tr>
                        );
                      })
                    )}
                  </AnimatePresence>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              gap: { xs: 2, sm: 0 }
            }}>
              <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                Showing {breaklistData.length > 0 ? startIndex + 1 : 0} to {Math.min(startIndex + rowsPerPage, breaklistData.length)} of {breaklistData.length} entries
              </Typography>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(event, page) => setCurrentPage(page)}
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    '&.Mui-selected': {
                      backgroundColor: '#12C1DC',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#0FABDB'
                      }
                    }
                  }
                }}
              />
            </Box>

          </Paper>
        </motion.div>

        {/* Set Rates Dialog */}
        <Dialog
          open={ratesDialogOpen}
          onClose={handleCloseRatesDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: '#f8f9fa', 
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 600,
            color: '#374151'
          }}>
            <StoreIcon sx={{ color: '#12C1DC' }} />
            {selectedStoreForPanel ? `${selectedStoreForPanel.name} - RATES` : 'Set Rates'}
          </DialogTitle>
          
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                  Lates:
                </Typography>
                <Switch
                  checked={ratesFormData.late}
                  onChange={(e) => handleRatesInputChange('late', e.target.checked)}
                  
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#10b981',
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#10b981',
                      },
                    },
                  }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                  Undertime:
                </Typography>
                <Switch
                  checked={ratesFormData.undertime}
                  onChange={(e) => handleRatesInputChange('undertime', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#10b981',
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#10b981',
                      },
                    },
                  }}
                />
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Daily Rate"
                  value={ratesFormData.daily}
                  onChange={(e) => handleRatesInputChange('daily', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.daily}
                  helperText={ratesFormErrors.daily}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Holiday Rate"
                  value={ratesFormData.holiday}
                  onChange={(e) => handleRatesInputChange('holiday', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.holiday}
                  helperText={ratesFormErrors.holiday}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Holiday OT Rate"
                  value={ratesFormData.holidayot}
                  onChange={(e) => handleRatesInputChange('holidayot', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.holidayot}
                  helperText={ratesFormErrors.holidayot}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Holiday Rest Day Rate"
                  value={ratesFormData.holidayrestday}
                  onChange={(e) => handleRatesInputChange('holidayrestday', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.holidayrestday}
                  helperText={ratesFormErrors.holidayrestday}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Holiday Rest Day OT Rate"
                  value={ratesFormData.holidayrestdayot}
                  onChange={(e) => handleRatesInputChange('holidayrestdayot', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.holidayrestdayot}
                  helperText={ratesFormErrors.holidayRestDayOTRate}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nightshift Rate"
                  value={ratesFormData.nightshift}
                  onChange={(e) => handleRatesInputChange('nightshift', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.nightshift}
                  helperText={ratesFormErrors.nightshift}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />

              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nightshift OT Rate"
                  value={ratesFormData.nightshiftot}
                  onChange={(e) => handleRatesInputChange('nightshiftot', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.nightshiftot}
                  helperText={ratesFormErrors.nightshiftot}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Holiday Nightshift Rate"
                  value={ratesFormData.holidaynightshift}
                  onChange={(e) => handleRatesInputChange('holidaynightshift', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.holidaynightshift}
                  helperText={ratesFormErrors.holidaynightshift}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Holiday Nightshift OT Rate"
                  value={ratesFormData.holidaynightshiftot}
                  onChange={(e) => handleRatesInputChange('holidaynightshiftot', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.holidaynightshiftot}
                  helperText={ratesFormErrors.holidaynightshiftot}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Special Holiday Nightshift Rate"
                  value={ratesFormData.specialnightshift}
                  onChange={(e) => handleRatesInputChange('specialnightshift', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.specialnightshift}
                  helperText={ratesFormErrors.specialnightshift}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Special Holiday Nightshift OT Rate"
                  value={ratesFormData.specialnightshiftot}
                  onChange={(e) => handleRatesInputChange('specialnightshiftot', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.specialnightshiftot}
                  helperText={ratesFormErrors.specialnightshiftot}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>


              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Overtime Rate"
                  value={ratesFormData.overtime}
                  onChange={(e) => handleRatesInputChange('overtime', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.overtime}
                  helperText={ratesFormErrors.overtime}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SSS Rate"
                  value={ratesFormData.sss}
                  onChange={(e) => handleRatesInputChange('sss', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.sss}
                  helperText={ratesFormErrors.sss}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Pag-ibig Rate"
                  value={ratesFormData.pagibig}
                  onChange={(e) => handleRatesInputChange('pagibig', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.pagibig}
                  helperText={ratesFormErrors.pagibig}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Philhealth Rate"
                  value={ratesFormData.philhealth}
                  onChange={(e) => handleRatesInputChange('philhealth', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.philhealth}
                  helperText={ratesFormErrors.philhealth}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Rest Day Duty Rate"
                  value={ratesFormData.restday}
                  onChange={(e) => handleRatesInputChange('restday', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.restday}
                  helperText={ratesFormErrors.restday}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Rest Day OT Rate"
                  value={ratesFormData.restdayot}
                  onChange={(e) => handleRatesInputChange('restdayot', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.restdayot}
                  helperText={ratesFormErrors.restdayot}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Special Holiday Rate"
                  value={ratesFormData.specialholiday}
                  onChange={(e) => handleRatesInputChange('specialholiday', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.specialholiday}
                  helperText={ratesFormErrors.specialholiday}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Special Holiday OT Rate"
                  value={ratesFormData.specialholidayot}
                  onChange={(e) => handleRatesInputChange('specialholidayot', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.specialholidayot}
                  helperText={ratesFormErrors.specialholidayot}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Special Holiday RD Rate"
                  value={ratesFormData.specialholidayrestday}
                  onChange={(e) => handleRatesInputChange('specialholidayrestday', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.specialholidayrestday}
                  helperText={ratesFormErrors.specialholidayrestday}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Special Holiday RD OT Rate"
                  value={ratesFormData.specialholidayrestdayot}
                  onChange={(e) => handleRatesInputChange('specialholidayrestdayot', parseFloat(e.target.value) || 0)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  error={!!ratesFormErrors.specialholidayrestdayot}
                  helperText={ratesFormErrors.specialholidayrestdayot}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Allowance"
                  value={ratesFormData.allowance}
                  onChange={(e) => handleRatesInputChange('allowance', e.target.value)}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    onWheel: (e) => e.target.blur() // ✅ THIS works
                  }}
                  variant="outlined"
                  size="small"
                  placeholder="Allowance"
                  error={!!ratesFormErrors.allowance}
                  helperText={ratesFormErrors.allowance}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ p: 3, bgcolor: '#f8f9fa', borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={handleCloseRatesDialog}
              variant="outlined"
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                '&:hover': {
                  borderColor: '#9ca3af',
                  bgcolor: '#f9fafb'
                }
              }}
            >
              Close
            </Button>
            <Button
              onClick={handleRatesFormSubmit}
              variant="contained"
              disabled={savingRates}
              sx={{
                bgcolor: '#10b981',
                '&:hover': { bgcolor: '#059669' },
                '&:disabled': {
                  bgcolor: '#e5e7eb',
                  color: '#9ca3af'
                }
              }}
            >
              {savingRates ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Rates Confirmation Dialog */}
        <Dialog
          open={deleteRatesDialogOpen}
          onClose={handleCloseDeleteRatesDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: '#fef2f2', 
            borderBottom: '1px solid #fecaca',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 600,
            color: '#dc2626'
          }}>
            <DeleteIcon sx={{ color: '#dc2626' }} />
            Delete Rates Confirmation
          </DialogTitle>
          
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, color: '#374151' }}>
              Are you sure you want to delete the rates for:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#dc2626', mb: 2 }}>
              {selectedStoreForPanel?.name}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              This action cannot be undone. All rate configurations for this store will be permanently removed.
            </Typography>
          </DialogContent>
          
          <DialogActions sx={{ p: 3, bgcolor: '#f8f9fa', borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={handleCloseDeleteRatesDialog}
              variant="outlined"
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                '&:hover': {
                  borderColor: '#9ca3af',
                  bgcolor: '#f9fafb'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDeleteRates}
              variant="contained"
              disabled={savingRates}
              sx={{
                bgcolor: '#dc2626',
                '&:hover': { bgcolor: '#b91c1c' },
                '&:disabled': {
                  bgcolor: '#e5e7eb',
                  color: '#9ca3af'
                }
              }}
            >
              {savingRates ? 'Deleting...' : 'Delete Rates'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Manage Allowances Dialog */}
        <Dialog
          open={allowancesDialogOpen}
          onClose={handleCloseAllowancesDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: '#f8f9fa', 
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 600,
            color: '#374151'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StoreIcon sx={{ color: '#12C1DC' }} />
              {selectedStoreForPanel ? `${selectedStoreForPanel.name} - Allowances` : 'Manage Allowances'}
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenAllowanceDialog('add')}
              sx={{
                bgcolor: '#12C1DC',
                '&:hover': { bgcolor: '#0FABDB' },
                textTransform: 'none'
              }}
            >
              Create Allowance
            </Button>
          </DialogTitle>
          
          <DialogContent sx={{ p: 3 }}>
            {/* Search */}
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search allowances..."
              value={allowanceSearchTerm}
              onChange={(e) => setAllowanceSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9ca3af' }} />
                  </InputAdornment>
                ),
                endAdornment: allowanceSearchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setAllowanceSearchTerm('')}
                      sx={{ color: '#9ca3af' }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2, width: '100%' }}
            />

            {/* Allowances Table */}
            {loadingAllowances ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Amount</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell><strong>Active</strong></TableCell>
                      <TableCell><strong>Taxable</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Admin Fee</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allowances
                      .filter(allowance => {
                        const searchLower = allowanceSearchTerm.toLowerCase();
                        return allowance.name.toLowerCase().includes(searchLower) ||
                          (allowance.description || '').toLowerCase().includes(searchLower);
                      })
                      .map((allowance) => {
                        const isAssigned = allowance.assignments?.stores?.includes(selectedStoreForPanel?.name);
                        return (
                          <TableRow key={allowance._id} hover>
                            <TableCell>{allowance.name}</TableCell>
                            <TableCell>
                              ₱{allowance.amount || 0}
                            </TableCell>
{/*                            <TableCell>
                              {allowance.type === 'fixed_amount'
                                ? `₱${(allowance.amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : `${(allowance.rate || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`
                              }
                            </TableCell>*/}
                            <TableCell>{allowance.description || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={allowance.is_active ? 'Yes' : 'No'}
                                size="small"
                                sx={{
                                  bgcolor: allowance.is_active ? '#e3f2fd' : '#f3e5f5',
                                  color: allowance.is_active ? '#1976d2' : '#7b1fa2'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={allowance.is_taxable ? 'Yes' : 'No'}
                                size="small"
                                sx={{
                                  bgcolor: allowance.is_taxable ? '#e3f2fd' : '#f3e5f5',
                                  color: allowance.is_taxable ? '#1976d2' : '#7b1fa2'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={allowance.type === 'billing' ? 'Less to billing' : allowance.type === 'gross_billing' ? 'Add to Gross billing' : 'Charge to client'}
                                size="small"
                                sx={{
                                  bgcolor: allowance.type === 'billing' ? '#e3f2fd' : allowance.type === 'gross_billing' ? '#e8f5e9' : '#f3e5f5',
                                  color: allowance.type === 'billing' ? '#1976d2' : allowance.type === 'gross_billing' ? '#2e7d32' : '#7b1fa2'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={allowance.admin ? 'Yes' : 'No'}
                                size="small"
                                sx={{
                                  bgcolor: allowance.admin ? '#e3f2fd' : '#f3e5f5',
                                  color: allowance.admin ? '#1976d2' : '#7b1fa2'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenAllowanceDialog('edit', allowance)}
                                    sx={{ color: '#12C1DC' }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteAllowance(allowance)}
                                    sx={{ color: '#ef4444' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {allowances.filter(allowance => {
                      const searchLower = allowanceSearchTerm.toLowerCase();
                      return allowance.name.toLowerCase().includes(searchLower) ||
                        (allowance.description || '').toLowerCase().includes(searchLower);
                    }).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            No allowances found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAllowancesDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Allowance Form Dialog */}
        <Dialog
          open={allowanceDialogOpen}
          onClose={handleCloseAllowanceDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {allowanceDialogMode === 'add' ? 'Create New Allowance' : 'Edit Allowance'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Allowance Name"
                value={allowanceFormData.name}
                onChange={(e) => setAllowanceFormData({ ...allowanceFormData, name: e.target.value })}
                error={!!allowanceFormErrors.name}
                helperText={allowanceFormErrors.name}
                fullWidth
                required
                disabled={saving}
              />
{/*
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={allowanceFormData.type}
                  onChange={(e) => setAllowanceFormData({ ...allowanceFormData, type: e.target.value })}
                  label="Type"
                  disabled={saving}
                >
                  <MenuItem value="fixed_amount">Fixed Amount</MenuItem>
                  <MenuItem value="rate_based">Rate Based</MenuItem>
                </Select>
              </FormControl>*/}
              <TextField
                label="Amount"
                type="number"
                value={allowanceFormData.amount}
                onChange={(e) => setAllowanceFormData({ ...allowanceFormData, amount: parseFloat(e.target.value) || 0 })}
                error={!!allowanceFormErrors.amount}
                helperText={allowanceFormErrors.amount}
                fullWidth
                required
                disabled={saving}
                inputProps={{ min: 0, step: 0.01 }}
              />
              {/*{allowanceFormData.type === 'fixed_amount' ? (
                <TextField
                  label="Amount"
                  type="number"
                  value={allowanceFormData.amount}
                  onChange={(e) => setAllowanceFormData({ ...allowanceFormData, amount: parseFloat(e.target.value) || 0 })}
                  error={!!allowanceFormErrors.amount}
                  helperText={allowanceFormErrors.amount}
                  fullWidth
                  required
                  disabled={saving}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              ) : (
                <TextField
                  label="Rate"
                  type="number"
                  value={allowanceFormData.rate}
                  onChange={(e) => setAllowanceFormData({ ...allowanceFormData, rate: parseFloat(e.target.value) || 0 })}
                  error={!!allowanceFormErrors.rate}
                  helperText={allowanceFormErrors.rate}
                  fullWidth
                  required
                  disabled={saving}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              )}
*/}
              <TextField
                label="Description"
                value={allowanceFormData.description}
                onChange={(e) => setAllowanceFormData({ ...allowanceFormData, description: e.target.value })}
                multiline
                rows={3}
                fullWidth
                disabled={saving}
              />

              <MuiFormControlLabel
                control={
                  <Switch
                    checked={allowanceFormData.is_active}
                    onChange={(e) => setAllowanceFormData({ ...allowanceFormData, is_active: e.target.checked })}
                    disabled={saving}
                  />
                }
                label="Active"
              />

              <MuiFormControlLabel
                control={
                  <Switch
                    checked={allowanceFormData.is_taxable}
                    onChange={(e) => setAllowanceFormData({ ...allowanceFormData, is_taxable: e.target.checked })}
                    disabled={saving}
                  />
                }
                label="Taxable"
              />
              <MuiFormControlLabel
                control={
                  <Switch
                    checked={allowanceFormData.admin}
                    onChange={(e) => {
                      const newAdmin = e.target.checked;
                      setAllowanceFormData({ 
                        ...allowanceFormData, 
                        admin: newAdmin,
                        // Reset type to billing if admin is turned off while gross_billing is selected
                        type: !newAdmin && allowanceFormData.type === 'gross_billing' ? 'billing' : allowanceFormData.type
                      });
                    }}
                    disabled={saving}
                  />
                }
                label="Admin fee"
              />
              <RadioGroup
                value={allowanceFormData.type}
                onChange={(e) =>
                  setAllowanceFormData({
                    ...allowanceFormData,
                    type: e.target.value,
                  })
                }
              >
                <FormControlLabel
                  value="client"
                  control={<Radio disabled={saving} />}
                  label="Charge to client"
                />
                <FormControlLabel
                  value="billing"
                  control={<Radio disabled={saving} />}
                  label="Less to billing"
                />
                {allowanceFormData.admin && (
                  <FormControlLabel
                    value="gross_billing"
                    control={<Radio disabled={saving} />}
                    label="Add to Gross billing"
                  />
                )}
              </RadioGroup>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseAllowanceDialog}
              disabled={savingAllowance}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAllowanceFormSubmit}
              variant="contained"
              disabled={savingAllowance}
              sx={{
                bgcolor: '#12C1DC',
                '&:hover': { bgcolor: '#0FABDB' }
              }}
            >
              {savingAllowance ? 'Saving...' : (allowanceDialogMode === 'add' ? 'Create' : 'Update')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default Stores;
